import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as PersonnelDetailActions from './personnel-detail.actions';
import {
	FieldWorker,
	FieldWorkerPayrollType,
	JobAssignment,
	PagedResultsOfAuditTrail
} from 'src/shared/api';

export const personnelDetailFeatureKey = 'personnel-detail';

export interface PersonnelDetailsState extends EntityState<FieldWorker> {
	selectedPersonnelId?: number;
	error?: any;
	isLoading?: boolean;
	isSaving?: boolean;
	payrollType?: FieldWorkerPayrollType;
	fieldWorkerTypeId?: number;
	shouldStayOnPage?: boolean;
	isHistoryLoading?: boolean;
	pagedHistoryResults?: PagedResultsOfAuditTrail;
	assignments?: JobAssignment[]
}

export const personnelDetailAdapter: EntityAdapter<FieldWorker> = createEntityAdapter<
	FieldWorker
>();

export interface PersonnelDetailPartialState {
	readonly [personnelDetailFeatureKey]: PersonnelDetailsState;
}

export const initialState: PersonnelDetailsState = personnelDetailAdapter.getInitialState(
	{
		isLoading: true,
		isSaving: false,
		pagedHistoryResults: { results: [], resultCount: 0 }
		// additional entity state properties
	}
);

const personnelReducer = createReducer(
	initialState,
	on(
		PersonnelDetailActions.personnelLoadedFromStore,
		PersonnelDetailActions.newPersonnelContinued,
		(state) => ({ ...state, isLoading: false })
	),
	on(PersonnelDetailActions.saveRequested, (state, { shouldStayOnPage }) => ({
		...state,
		isSaving: true,
		shouldStayOnPage,
	})),
	on(PersonnelDetailActions.newPersonnelSaved, (state) =>
		personnelDetailAdapter.removeOne(0, state)
	),
	on(PersonnelDetailActions.newPersonnelSaved, (state, { personnel }) =>
		personnelDetailAdapter.addOne(personnel, { ...state, isSaving: false })
	),
	on(PersonnelDetailActions.personnelSaved, (state, { personnel }) =>
		personnelDetailAdapter.updateOne(
			{
				id: personnel.id,
				changes: {
					...personnel,
				},
			},
			{ ...state, isSaving: false }
		)
	),
	on(PersonnelDetailActions.personnelSelected, (state, { personnelId }) => ({
		...state,
		selectedPersonnelId: personnelId,
	})),
	on(PersonnelDetailActions.personnelLoadedFromApi, (state, { personnel }) =>
		personnelDetailAdapter.upsertOne(personnel, {
			...state,
			isLoading: false,
		})
	),
	on(PersonnelDetailActions.assignmentsLoaded, (state, { assignments }) => ({
		...state,
		assignments
	})),
	on(PersonnelDetailActions.errorSavingPersonnel, (state, { error }) => ({
		...state,
		error,
		isSaving: false,
	})),
	on(PersonnelDetailActions.newPersonnelStarted, (state) =>
		personnelDetailAdapter.addOne({ id: 0 }, { ...state, isLoading: false })
	),
	on(PersonnelDetailActions.payrollTypeUpdated, (state, { payrollType }) => ({
		...state,
		payrollType,
	})),
	on(
		PersonnelDetailActions.fieldWorkerTypeUpdated,
		(state, { fieldWorkerTypeId }) => ({
			...state,
			fieldWorkerTypeId,
		})
	),
	on(PersonnelDetailActions.personnelDetailDestroyed, (state) => ({
		...state,
		isLoading: true,
		isLoadingLogo: true,
		payrollType: undefined,
		error: undefined,
		pagedHistoryResults: { results: [], resultCount: 0 },
		assignments: undefined
	})),
	on(PersonnelDetailActions.historyRequested, (state) => ({
		...state,
		isHistoryLoading: true,
	})),
	on(PersonnelDetailActions.historyLoaded, (state, { pagedHistoryResults }) => ({
		...state,
		pagedHistoryResults,
		isHistoryLoading: false,
	}))
);

export function reducer(
	state: PersonnelDetailsState | undefined,
	action: Action
) {
	return personnelReducer(state, action);
}
